import { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  ArrowPathIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";
import { OrganizationV3 } from "../../../store/slices/OrganizationsV3Slice";
import {
  fetchOrganizationCredentials,
  OrganizationCredentials,
  rotateOrganizationCredentials,
} from "../../../services/OrganizationsV3Service";
import { CREDENTIALS_ROTATION_DAYS } from "../../../constants/organizations";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "../../Catalyst/dialog";
import { Badge } from "../../Catalyst/badge";
import { Button } from "../../Catalyst/button";
import { Input } from "../../Catalyst/input";
import { useToast } from "../../../contexts/ToastContext";
import { formatStatusDate, isCredentialRotationOverdue } from "./utils";

const ButtonSpinner = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

// Error envelopes are shaped { "msg": "..." } (403 "You do not have access to
// this resource", 404 "Organization N not found"); fall back to friendly copy
// rather than axios's "Request failed with status code 500".
const toErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as { response?: { data?: { msg?: string } } } | null;
  return err?.response?.data?.msg || fallback;
};

// Small square icon button used beside the read-only fields (copy, show/hide).
const FieldButton = ({
  label,
  onClick,
  pressed,
  children,
}: {
  label: string;
  onClick: () => void;
  pressed?: boolean;
  children: React.ReactNode;
}) => (
  <Button outline onClick={onClick} aria-label={label} title={label} aria-pressed={pressed}>
    {children}
  </Button>
);

interface OrgCredentialsDialogProps {
  organization: OrganizationV3 | null;
  open: boolean;
  onClose: () => void;
}

// Reveal and rotate an organization's OAuth client credentials (Super Admin
// only). State is deliberately local, not Redux: the plaintext secret lives
// in this component only while the dialog is open and is dropped on close.
// Both API calls are audited server-side, so the GET fires once per open (on
// the explicit "View credentials" click) and never on focus or a timer; the
// show/hide toggle is purely client-side.
const OrgCredentialsDialog = ({ organization, open, onClose }: OrgCredentialsDialogProps) => {
  const { showSuccessToast, showErrorToast } = useToast();

  const [credentials, setCredentials] = useState<OrganizationCredentials | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  // Bumped by "Try again" so a failed reveal can be retried with another explicit click.
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [secretVisible, setSecretVisible] = useState(false);
  // "confirm" swaps the body for the rotation warning before anything is sent.
  const [step, setStep] = useState<"view" | "confirm">("view");
  const [isRotating, setIsRotating] = useState(false);
  const [rotateError, setRotateError] = useState("");
  // After a rotation the new secret is shown unmasked once with a "copy now" nudge.
  const [justRotated, setJustRotated] = useState(false);

  // Depend on the id, not the row object, so a list refetch while the dialog is
  // open cannot trigger a second (audited) GET.
  const orgId = organization?.id;

  useEffect(() => {
    if (!open || orgId == null) return undefined;
    let cancelled = false;
    setCredentials(null);
    setLoadError("");
    setIsLoading(true);
    setSecretVisible(false);
    setStep("view");
    setRotateError("");
    setJustRotated(false);
    fetchOrganizationCredentials(orgId)
      .then((result) => {
        if (!cancelled) setCredentials(result);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(toErrorMessage(error, "We couldn't load the credentials."));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    // Ignore a response that lands after the dialog closed or moved to another org.
    return () => {
      cancelled = true;
    };
  }, [open, orgId, loadAttempt]);

  const handleClose = () => {
    // Closing mid-rotation would leave the new secret unseen with no way to recover it
    // short of another rotation.
    if (isRotating) return;
    // Drop the secret from memory as soon as the dialog goes away.
    setCredentials(null);
    setSecretVisible(false);
    setStep("view");
    setJustRotated(false);
    onClose();
  };

  const copyToClipboard = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showSuccessToast("Copied", `${label} copied to clipboard.`);
    } catch {
      showErrorToast("Copy failed", `Could not copy the ${label.toLowerCase()} to the clipboard.`);
    }
  };

  const handleRotate = async () => {
    if (orgId == null || isRotating) return;
    setIsRotating(true);
    setRotateError("");
    try {
      const result = await rotateOrganizationCredentials(orgId);
      setCredentials(result);
      setSecretVisible(true);
      setJustRotated(true);
      setStep("view");
    } catch (error) {
      // Keep the confirm step open so the admin can retry or back out.
      setRotateError(toErrorMessage(error, "We couldn't rotate the secret. Nothing was changed."));
    } finally {
      setIsRotating(false);
    }
  };

  const orgName = credentials?.name ?? organization?.name ?? "this organization";
  const hasCredentials = !!credentials?.clientId;
  // An org with no credentials gets both issued by the same endpoint.
  const rotateLabel = hasCredentials ? "Rotate secret" : "Generate credentials";
  const rotationOverdue = hasCredentials && isCredentialRotationOverdue(credentials?.lastRotatedAt);
  const lastRotatedText = credentials?.lastRotatedAt
    ? `Last rotated ${formatStatusDate(credentials.lastRotatedAt) ?? credentials.lastRotatedAt}`
    : "Never rotated";

  const renderCredentials = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-3 py-2 text-sm text-textColor">
          <ButtonSpinner />
          Loading credentials...
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm font-medium text-red-500">{loadError}</p>
          <Button outline onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
            <ArrowPathIcon />
            Try again
          </Button>
        </div>
      );
    }

    if (!credentials) return null;

    if (!hasCredentials) {
      return (
        <div className="rounded-lg border border-shadowborder bg-[#F9FAFB] p-4">
          <p className="text-sm font-medium text-texthead">No credentials issued</p>
          <p className="mt-1 text-sm text-textColor">
            This organization has never been issued a client ID and secret. Generate them to let a
            partner integrate with the API.
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-5">
        {justRotated && (
          <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-700">
            <span className="font-medium">New secret issued.</span> Copy it now: it is hidden when this
            dialog closes, and every later reveal is recorded in the audit trail.
          </div>
        )}

        <div>
          <label htmlFor="org-client-id" className="text-sm font-medium text-texthead">
            Client ID
          </label>
          <div className="mt-2 flex items-center gap-2">
            <Input
              id="org-client-id"
              name="clientId"
              value={credentials.clientId ?? ""}
              readOnly
              className="font-mono"
            />
            <FieldButton
              label="Copy client ID"
              onClick={() => copyToClipboard("Client ID", credentials.clientId ?? "")}
            >
              <ClipboardDocumentIcon />
            </FieldButton>
          </div>
        </div>

        <div>
          <label htmlFor="org-client-secret" className="text-sm font-medium text-texthead">
            Client secret
          </label>
          <div className="mt-2 flex items-center gap-2">
            {/* The toggle only changes the input type; the value is already in memory. */}
            <Input
              id="org-client-secret"
              name="clientSecret"
              type={secretVisible ? "text" : "password"}
              value={credentials.clientSecret ?? ""}
              readOnly
              autoComplete="off"
              className="font-mono"
            />
            <FieldButton
              label={secretVisible ? "Hide secret" : "Show secret"}
              pressed={secretVisible}
              onClick={() => setSecretVisible((visible) => !visible)}
            >
              {secretVisible ? <EyeSlashIcon /> : <EyeIcon />}
            </FieldButton>
            <FieldButton
              label="Copy client secret"
              onClick={() => copyToClipboard("Client secret", credentials.clientSecret ?? "")}
            >
              <ClipboardDocumentIcon />
            </FieldButton>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm text-textColor">
          <span>{lastRotatedText}</span>
          {rotationOverdue && (
            <Badge
              color="amber"
              title={`The partner documentation asks for the secret to be rotated every ${CREDENTIALS_ROTATION_DAYS} days.`}
            >
              <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
              Rotation due
            </Badge>
          )}
        </div>
      </div>
    );
  };

  const renderConfirm = () => (
    <div className="flex gap-3 rounded-lg border border-amber-500/30 bg-amber-400/10 p-4">
      <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
      <div className="text-sm text-texthead">
        {hasCredentials ? (
          <>
            <p className="font-medium">The current secret stops working immediately.</p>
            <p className="mt-1 text-textColor">
              The partner&rsquo;s integration will fail until they update to the new secret.
              Already-issued tokens keep working for up to 24 hours. The client ID does not change.
            </p>
          </>
        ) : (
          <>
            <p className="font-medium">Generate credentials for &ldquo;{orgName}&rdquo;?</p>
            <p className="mt-1 text-textColor">
              This issues a client ID and secret for the organization. Share them with the partner
              over a secure channel.
            </p>
          </>
        )}
      </div>
    </div>
  );

  return (
    // Ignore backdrop/Escape while rotating so the dialog cannot vanish mid-request.
    <Dialog size="xl" open={open} onClose={handleClose}>
      <div className="flex items-start justify-between gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-shadowborder bg-white shadow-light-sm">
          <KeyIcon className="h-6 w-6 text-texthead" aria-hidden="true" />
        </span>
        <button
          type="button"
          onClick={handleClose}
          disabled={isRotating}
          aria-label="Close"
          className="shrink-0 rounded-md p-1 text-zinc-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <DialogTitle className="mt-4">
        {step === "confirm" && hasCredentials
          ? `Rotate the secret for “${orgName}”?`
          : `Client credentials for “${orgName}”`}
      </DialogTitle>
      <DialogDescription>
        Partners use these on the token endpoint to obtain an organization token. Viewing and
        rotating them is recorded in the organization&rsquo;s audit trail.
      </DialogDescription>
      <DialogBody>
        {step === "confirm" ? renderConfirm() : renderCredentials()}
        {rotateError && <p className="mt-4 text-sm font-medium text-red-500">{rotateError}</p>}
      </DialogBody>
      <DialogActions>
        {step === "confirm" ? (
          <>
            <Button
              outline
              onClick={() => {
                // A failed attempt's message refers to the confirm step; drop it with the step.
                setRotateError("");
                setStep("view");
              }}
              disabled={isRotating}
            >
              Back
            </Button>
            <Button
              color={hasCredentials ? "red" : "dark"}
              onClick={handleRotate}
              disabled={isRotating}
            >
              {isRotating && <ButtonSpinner />}
              {isRotating ? (hasCredentials ? "Rotating..." : "Generating...") : rotateLabel}
            </Button>
          </>
        ) : (
          <>
            <Button outline onClick={handleClose}>
              Close
            </Button>
            {credentials && (
              <Button
                color="dark"
                onClick={() => {
                  setRotateError("");
                  setStep("confirm");
                }}
              >
                <ArrowPathIcon />
                {rotateLabel}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrgCredentialsDialog;
